import { Box, Button, Grid, Stack, Tab, Tabs, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { setSnackbar } from "../../../util/state/globalAction";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../util/state/globalVariables";
import { createCustomerBase, getCustomerBaseById } from "../api/service/customerbaseApi";
import { cbContactPersonToDeleteArrayState, cbErrorSectionValidationState, defaultBuagClassesState, defaultSalesAreasState, googleSearchBarClearValueState, isGlobalEditModeFormDirtyState, isGlobalEditModeState, isMobilityVoucherModeState, openCustomerBaseModal, queryFieldsState } from "../state/customerbaseState";
import {
    CBAddressType,
    CBCountry,
    CBCreationOfProps,
    CBExtraAddressAction,
    CBExtraAddressDeltaFields,
    CBPartnerType,
    CBRoleKey,
    CBSapCode,
    CBTabIndex,
    CBValuableLength,
    CBYesNoType,
    CustomerBaseDto,
    IEditCustomerBaseFieldsParams,
    IFormValues
} from "../types";
import { getErrorKey, getInvoicingAddress, getResidenceAddress, extraAddressDtoToForm, isFranceOrganization, shouldAskEmailScope } from "../utils";
import CustomerBaseDetailsTab from "./CustomerBaseDetailsTab";
import CustomLoader from "../../../util/component/CustomLoader";
import CBMultiSectionActionButtons from "./CBMultiSectionActionButtons";
import CustomerBaseDossiersTab from "./dossier/CustomerBaseDossiersTab";
import CustomerBaseCarSharingTab from "./carSharing/CustomerBaseCarSharingTab";
import CustomerBaseChangeLogTab from "./CustomerBaseChangeLogTab";
import CustomerBaseModal from "./modal/CustomerBaseModal";
import { useHasScope } from "../../../config/globalConfig";
import { OperationScope } from "../../operation/config";


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}

        </div>
    );

}

function allyProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

interface CustomerBaseTabsManagerProps extends CBCreationOfProps {
    onCustomerSubmit?: (customer: any) => void
}

const CustomerBaseTabsManager = ({ creationOf, onCustomerSubmit }: CustomerBaseTabsManagerProps) => {

    const navigate = useNavigate();
    const { dispatchGlobalData, globalData } = useContext(GlobalContext);
    const { t } = useTranslation(["customerbase", "translation"]);
    const isCreationMode = !!creationOf;
    const [activeTab, setActiveTab] = useState(CBTabIndex.CUSTOMER_DETAIL);
    const formContext = useFormContext<IFormValues>();
    const formState = useFormState<IFormValues>();
    /**
     * CR-DRIVALIA-EPCH (LPP-22837): valori in attesa della scelta dell'operatore sullo scope
     * del cambio e-mail (contatti / anche fatturazione). Non nullo = modale aperta.
     */
    const [pendingEmailScopeValues, setPendingEmailScopeValues] = useState<any | null>(null);
    const setOpenCustomerBaseModal = useSetRecoilState(openCustomerBaseModal);
    const [queryFields, setQueryFields] = useState<IEditCustomerBaseFieldsParams>();
    const setEditQueryFields = useSetRecoilState(queryFieldsState);

    const partnerType = useWatch<IFormValues, 'cbDetails.partnerType'>({ name: "cbDetails.partnerType" });

    const csCustomerId = useWatch<IFormValues, 'csData.csCustomerId'>({name: "csData.csCustomerId"});

    const cbCustomerId = useWatch<IFormValues, 'cbDetails.cbCustomerId'>({name: "cbDetails.cbCustomerId"});

    const showCarSharingTab = !!csCustomerId;

    const setGoogleSearchBarClearValueState = useSetRecoilState(googleSearchBarClearValueState);

    const setIsGlobalEditMode = useSetRecoilState(isGlobalEditModeState);
    const isGlobalEditModeFormDirty = useRecoilValue(isGlobalEditModeFormDirtyState);

    const cbContactPersonToDeleteArray = useRecoilValue(cbContactPersonToDeleteArrayState);
    const setCbContactPersonToDeleteArray = useSetRecoilState(cbContactPersonToDeleteArrayState);

    const [cbIdToSearchBySapKo, setCbIdToSearchBySapKo] = useState('');

    const setErrorSection = useSetRecoilState(cbErrorSectionValidationState);

    const [defaultExtraAddresses, setDefaultExtraAddresses] = useState<any[]>([]);

    const isMobilityVoucherMode = useRecoilValue(isMobilityVoucherModeState);

    const setDefaultSalesAreas = useSetRecoilState(defaultSalesAreasState);
    const setDefaultBuagClasses = useSetRecoilState(defaultBuagClassesState);

    const hasCarSharingScope = useHasScope(OperationScope.ViewCsVehicleScope);

    const handleChangeTab = (_e: any, newValue: number) => {

        if (!isCreationMode && isGlobalEditModeFormDirty) setOpenCustomerBaseModal(true);
        else {
            setIsGlobalEditMode(false);
            setActiveTab(newValue);

        }
    };

    /**
     * CR-DRIVALIA-EPCH (LPP-22837): `applyEmailToInvoicing` porta fino alla composizione degli
     * indirizzi la scelta fatta dall'operatore quando l'e-mail di contatto e' usata anche
     * sull'indirizzo di fatturazione (vedi `saveWithEmailScopeCheck`).
     */
    const onSubmit = (params: any, applyEmailToInvoicing: boolean = false) => {

        const isOrganization = params.cbDetails.partnerType == CBPartnerType.ORGANIZATION;
        const addresses: any[] = [
            {
                id: params.cbPersonalInfo.residenceAddressId || null,
                addressNumber: params.cbPersonalInfo.residenceAddressNumber || null,
                cbCustomerId: params.cbDetails.cbCustomerId || null,
                street: params.cbPersonalInfo.residenceStreet || null,
                houseNumber: params.cbPersonalInfo.residenceHouseNumber || null,
                zipCode: params.cbPersonalInfo.residenceZipCode || null,
                province: !!params.cbPersonalInfo.residenceCountry ? params.cbPersonalInfo.residenceProvince || null : null,
                city: params.cbPersonalInfo.residenceCity || null,
                country: params.cbPersonalInfo.residenceCountry || null,
                addrEmail: params.cbDetails.email || null,
                addrMobile: params.cbDetails.mobilePrefix && params.cbDetails.mobile ? (params.cbDetails.mobilePrefix + params.cbDetails.mobile) : null,
                addressType: params.cbPersonalInfo.residenceAddressType
            },
            {
                id: params.cbInvoicingAddress.invoicingAddressId || null,
                cbCustomerId: params.cbDetails.cbCustomerId || null,
                addressNumber: params.cbInvoicingAddress.invoicingAddressNumber || null,
                street: params.cbInvoicingAddress.invoicingStreet || null,
                houseNumber: params.cbInvoicingAddress.invoicingHouseNumber || null,
                zipCode: params.cbInvoicingAddress.invoicingZipCode || null,
                province: !!params.cbInvoicingAddress.invoicingCountry ? params.cbInvoicingAddress.invoicingProvince || null : null,
                city: params.cbInvoicingAddress.invoicingCity || null,
                country: isOrganization && !params.cbInvoicingAddress.invoicingCountry ? (params.cbPersonalInfo.residenceCountry || null) : (params.cbInvoicingAddress.invoicingCountry || null),
                // CR-DRIVALIA-EPCH (LPP-22837): se l'operatore ha scelto di sostituire entrambe,
                // la nuova e-mail di contatto viene applicata anche all'indirizzo di fatturazione.
                addrEmail: (applyEmailToInvoicing && !!params.cbDetails.email)
                    ? params.cbDetails.email
                    : (params.cbInvoicingAddress.invoicingAddrEmail || null),
                pec: params?.cbInvoicingAddress?.invoicingCountry == CBCountry.FRANCE ? undefined : params.cbInvoicingAddress.invoicingPec || null,
                addrMobile: params.cbInvoicingAddress.addrMobile || null,
                telephone: params.cbInvoicingAddress.invoicingTelephone || null,
                fax: params.cbInvoicingAddress.invoicingFax || null,
                website: params.cbInvoicingAddress.invoicingWebsite || null,
                companyFiscalCode: isOrganization ? (params.cbPersonalInfo.taxCode || null) : (params.cbInvoicingAddress.invoicingCompanyFiscalCode || null),
                recipientCode: params?.cbInvoicingAddress?.invoicingCountry == CBCountry.FRANCE ? undefined : params.cbInvoicingAddress.invoicingRecipientCode || null,
                addressType: params.cbInvoicingAddress.invoicingAddressType
            }
        ].filter((addr) => (addr.addressType == CBAddressType.RESIDENCE) || (
            addr.addressType == CBAddressType.INVOICING &&
            Object.values(addr).some((value: any) => !!value && value !== addr.addressType)
        ));

        const payments: any[] = params.cbPaymentInfo.map(((pay: any) => ({
            id: pay.id || null,
            buagId: pay.buagId || null,
            buagClass: pay.buagClass || null,
            iban: pay.iban || null,
            accountHolder: pay.accountHolder || null,
            referenceDetails: pay.referenceDetails || null,
            paymentMethod: pay.paymentMethod || null,
            paymentTerms: pay.paymentTerms || null,
            correspondingVariant: pay.correspondingVariant || null


        }))).filter((pay: any) => Object.values(pay).some((value: any) => !!value));
        /*const salesAreas: any[] = params.salesAreas.filter((sa: any) => !!sa.distributionChannel && !!sa.division);
        const corporateContactPersons: any[] = params.cbCorporateInfo.contactPersons.filter((cp: any) => !!cp.name && !!cp.surname);
        

        const editQ = {
            cbCustomerId: params.cbDetails.cbCustomerId,
            bpCode: params.cbDetails.bpCode || null,
            email: params.cbDetails.email || null,
            role: params.cbDetails.role || [],
            language: params.cbDetails.language || null,
            vip: params.cbDetails.vip || null,
            dealerType: params.cbPersonalInfo.dealerType || null,
            customerType: params.cbPersonalInfo.customerType || null,
            legalForm: params.cbPersonalInfo.legalForm || null,
            registry : {
                taxCode: params.cbPersonalInfo.taxCode || null, 
                vatNumberCountry: params.cbPersonalInfo.vatNumberCountry || null,
                vatNumber: params.cbPersonalInfo.vatNumber || null,
                businessName: params.cbPersonalInfo.businessName || null
            },
            contactInfo: {
                prefix: params.cbDetails.mobilePrefix || null,
                mobile: params.cbDetails.mobile || null,
                addresses: addresses
            },
            paymentInfo: payments || [],
            salesAreas: salesAreas || [],
            corporateInfo: corporateContactPersons.length > 0 ? {
                idZIT019: params.cbCorporateInfo.idZIT019 || null,
                maxRentalNum: params.cbCorporateInfo.maxRentalNum || null,
                contactPersons: corporateContactPersons || []
            } : undefined
        };

        console.log("Edit Query in Create: ", editQ);*/

        if (!!params.cbDetails.cbCustomerId) {

            var extraAddressesDelta: CBExtraAddressDeltaFields[] = [];
            const cbCustomerId = params.cbDetails.cbCustomerId

            //const defaultExtraAddresses = formState.defaultValues?.cbExtraAddresses || [];
            const currentExtraAddresses = params?.cbExtraAddresses || [];

            defaultExtraAddresses.forEach(def => {
                if (!!def && (currentExtraAddresses.findIndex((curr: any) => !!curr.idAssociation && (curr.idAssociation == def.idAssociation)) == -1)) {
                    extraAddressesDelta.push({
                        action: CBExtraAddressAction.UNLINK,
                        link: {
                            customerId: cbCustomerId,
                            customerIdToLink: def.customerId || null,
                        }
                    });
                }
            });

            currentExtraAddresses.filter((curr: any) => !curr.idAssociation).forEach((curr: any) => {
                extraAddressesDelta.push({
                    action: CBExtraAddressAction.LINK,
                    link: {
                        customerId: cbCustomerId,
                        customerIdToLink: curr.customerId || null,
                    }
                });
            });

            const salesAreas: any[] = params.salesAreas.map((sa: any) => ({
                id: sa.id || null,
                salesOrganization: sa.salesOrganization || null,
                distributionChannel: sa.distributionChannel || null,
                division: sa.division || null,
                currency: sa.currency || null
            }));

            if (isOrganization) {

                const corporateContactPersons: any[] = [...params.cbCorporateInfo.contactPersons.map((cp: any) => ({
                    id: cp.id || null,
                    bpCode: cp.bpCode || null,
                    name: !!cp.name && cp.name.length > CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH ? cp.name.slice(0, CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH) : cp.name || null,
                    surname: !!cp.surname && cp.surname.length > CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH ? cp.surname.slice(0, CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH) : cp.surname || null,
                    address: cp.address || null,
                    houseNumber: cp.houseNumber || null,
                    country: cp.country || null,
                    region: cp.region || null,
                    city: cp.city || null,
                    postalCode: cp.postalCode || null,
                    phoneNumber: cp.phoneNumber || null,
                    mobile: cp.mobile || null,
                    email: cp.email || null,
                    delete: cp.delete || null
                })).filter((cp: any) => !!cp.name && !!cp.surname),
                ...cbContactPersonToDeleteArray.map(cptd => ({
                    id: cptd.id || null,
                    bpCode: cptd.bpCode || null,
                    name: cptd.name || null,
                    surname: cptd.surname || null,
                    address: cptd.address || null,
                    houseNumber: cptd.houseNumber || null,
                    country: cptd.country || null,
                    region: cptd.region || null,
                    city: cptd.city || null,
                    postalCode: cptd.postalCode || null,
                    phoneNumber: cptd.phoneNumber || null,
                    mobile: cptd.mobile || null,
                    email: cptd.email || null,
                    delete: cptd.delete
                }))];

                setEditQueryFields({
                    cbCustomerId: params.cbDetails.cbCustomerId,
                    bpCode: params.cbDetails.bpCode || null,
                    email: params.cbDetails.email || null,
                    role: params.cbDetails.role || [],
                    language: params.cbDetails.language || null,
                    vip: params.cbDetails.vip || null,
                    extMgmtReason: params.cbDetails.extMgmtReason || null,
                    dealerType: params.cbPersonalInfo.dealerType || null,
                    customerType: params.cbPersonalInfo.customerType || null,
                    legalForm: params.cbPersonalInfo.legalForm || null,
                    publicAuthority: params.cbPersonalInfo.publicAuthority || CBYesNoType.NO,
                    paIdNumber: params.cbPersonalInfo.paIdNumber || null,
                    registry: {
                        taxCode: params.cbPersonalInfo.taxCode || null,
                        vatNumberCountry: params.cbPersonalInfo.vatNumberCountry || null,
                        vatNumber: params.cbPersonalInfo.vatNumber || null,
                        businessName: params.cbPersonalInfo.businessName || null,
                        siren: isFranceOrganization(params, false) ? params.cbPersonalInfo.siren || null : undefined,
                        siret: isFranceOrganization(params, false) ? params.cbPersonalInfo.siret || undefined : undefined,
                    },
                    contactInfo: {
                        prefix: params.cbDetails.mobilePrefix || null,
                        mobile: params.cbDetails.mobile || null,
                        addresses: addresses
                    },
                    paymentInfo: payments || [],
                    salesAreas: salesAreas || [],
                    corporateInfo: corporateContactPersons.length > 0 ? {
                        idZIT019: params.cbCorporateInfo.idZIT019 || null,
                        maxRentalNum: params.cbCorporateInfo.maxRentalNum || null,
                        contactPersons: corporateContactPersons || []
                    } : undefined,
                    modifiedLinkedAddresses: extraAddressesDelta.length > 0 ? extraAddressesDelta : undefined
                });

            } else {
                setEditQueryFields({
                    cbCustomerId: params.cbDetails.cbCustomerId,
                    bpCode: params.cbDetails.bpCode || null,
                    email: params.cbDetails.email || null,
                    role: params.cbDetails.role || [],
                    language: params.cbDetails.language || null,
                    vip: params.cbDetails.vip || null,
                    extMgmtReason: params.cbDetails.extMgmtReason || null,
                    publicAuthority: params.cbPersonalInfo.publicAuthority || CBYesNoType.NO,
                    registry: {
                        firstName: params.cbPersonalInfo.firstName || null,
                        lastName: params.cbPersonalInfo.lastName || null,
                        birthDate: params.cbPersonalInfo.birthDate || null,
                        gender: params.cbPersonalInfo.gender || null,
                        birthCountry: params.cbPersonalInfo.birthCountry || null,
                        birthProvince: !!params.cbPersonalInfo.birthCountry ? params.cbPersonalInfo.birthProvince || null : null,
                        birthCity: params.cbPersonalInfo.birthCity || null,
                        taxCode: params.cbPersonalInfo.taxCode || null,
                        drvLicenceNumber: params.cbPersonalInfo.drvLicenceNumber || null,
                        vatNumberCountry: params.cbPersonalInfo.vatNumberCountry || null,
                        vatNumber: params.cbPersonalInfo.vatNumber || null,
                        businessName: params.cbPersonalInfo.businessName || null
                    },
                    contactInfo: {
                        prefix: params.cbDetails.mobilePrefix || null,
                        mobile: params.cbDetails.mobile || null,
                        addresses: addresses
                    },
                    paymentInfo: payments || [],
                    salesAreas: salesAreas || [],
                    modifiedLinkedAddresses: extraAddressesDelta.length > 0 ? extraAddressesDelta : undefined
                });
            }

        }
        else {

            setQueryFields({
                idTenant: globalData?.user?.legalEntities[0]?.name || "drivalia_it",
                email: params.cbDetails.email || null,
                cbType: "0", //params.cbDetails.cbType || null,
                dealerType: params.cbPersonalInfo.dealerType || null,
                //customerType: params.cbDetails.customerType || null,
                customerType: (partnerType == CBPartnerType.ORGANIZATION) ? params.cbPersonalInfo.customerType || null : "Private",
                role: params.cbDetails.role || [],
                //role: ["ZIT008"],
                language: params.cbDetails.language || null,
                partnerType: params.cbDetails.partnerType || null,
                vip: params.cbDetails.vip || null,
                extMgmtReason: params.cbDetails.extMgmtReason || null,
                legalForm: params.cbPersonalInfo.legalForm || null,
                publicAuthority: params.cbPersonalInfo.publicAuthority || CBYesNoType.NO,
                paIdNumber: params.cbPersonalInfo.paIdNumber || null,
                blacklist: params.cbDetails.blacklist || null,
                blacklistDescription: params.cbDetails.blacklistDescription || null,
                initialRegDate: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
                //finalRegDate: params.cbDetails.finalRegistrationDate || null,
                bpBlocked: params.cbDetails.bpBlocked || null,
                registry: {
                    firstName: params.cbPersonalInfo.firstName || null,
                    lastName: params.cbPersonalInfo.lastName || null,
                    gender: params.cbPersonalInfo.gender || null,
                    birthDate: params.cbPersonalInfo.birthDate || null,
                    birthCountry: params.cbPersonalInfo.birthCountry || null,
                    birthProvince: !!params.cbPersonalInfo.birthCountry ? params.cbPersonalInfo.birthProvince || null : null,
                    birthCity: params.cbPersonalInfo.birthCity || null,
                    drvLicenceNumber: params.cbPersonalInfo.drvLicenceNumber || null,
                    vatNumber: params.cbPersonalInfo.vatNumber || null,
                    vatNumberCountry: params.cbPersonalInfo.vatNumberCountry || null,
                    businessName: params.cbPersonalInfo.businessName || null,
                    taxCode: params.cbPersonalInfo.taxCode || null,
                    siren: isFranceOrganization(params, false) ? params.cbPersonalInfo.siren || undefined : undefined,
                    siret: isFranceOrganization(params, false) ? params.cbPersonalInfo.siret || undefined : undefined,
                },
                contactInfo: {
                    prefix: !!params.cbDetails.mobilePrefix ? params.cbDetails.mobilePrefix : null,
                    mobile: params.cbDetails.mobile || null,
                    addresses: addresses
                },
                salesAreas: params.salesAreas
                    .map((sa: any) => ({
                        salesOrganization: sa.salesOrganization || null,
                        distributionChannel: sa.distributionChannel || null,
                        division: sa.division || null,
                        currency: sa.currency || null
                    })) || [],
                termsAndCond: (partnerType == CBPartnerType.PERSON) ?
                    []
                    :
                    [...params.termsAndConditions
                        .filter((term: any) => term.result !== null && term.termCondType !== '' && term.division != '')
                        .map((tc: any) => ({
                            termCondType: tc.termCondType || null,
                            companyCode: tc.companyCode || null,
                            result: tc.result,
                            division: tc.division || null,
                            communicationChannel: tc.communicationChannel || null,
                            distributionChannel: tc.distributionChannel || null,
                            revocationChannel: tc.revocationChannel || null,
                            revocationReason: tc.revocationReason || null,
                            acquisitionDateTime: tc.acquisitionDateTime === "--" ? null : tc.acquisitionDateTime || null,
                            revocationDateTime: tc.revocationDateTime === "--" ? null : tc.revocationDateTime || null
                        })) || [],
                    ...params.cbConsents
                        .filter((cons: any) => cons.result !== null && cons.division != '')
                        .map((cons: any) => ({
                            termCondType: cons.termCondType || null,
                            companyCode: cons.companyCode || null,
                            result: cons.result,
                            division: cons.division || null,
                            communicationChannel: cons.communicationChannel || null,
                            distributionChannel: cons.distributionChannel || null,
                            revocationChannel: cons.revocationChannel || null,
                            revocationReason: cons.revocationReason || null,
                            acquisitionDateTime: cons.acquisitionDateTime === "--" ? null : cons.acquisitionDateTime || null,
                            revocationDateTime: cons.revocationDateTime || null
                        })) || []
                    ],
                paymentInfo: params.cbDetails?.role?.includes(CBRoleKey.SOLD_TO_PARTY) ? params.cbPaymentInfo
                    .filter((pay: any) => Object.values(pay).some((value: any) => value !== ''))
                    .map((pi: any) => ({
                        buagClass: pi.buagClass || null,
                        iban: !!pi.iban ? pi.iban.toUpperCase() || null : null,
                        accountHolder: pi.accountHolder || null,
                        referenceDetails: pi.referenceDetails || null,
                        paymentMethod: pi.paymentMethod || null,
                        paymentTerms: pi.paymentTerms || null,
                        correspondingVariant: pi.correspondingVariant || null
                    })) || [] : [],
                corporateInfo: (partnerType == CBPartnerType.ORGANIZATION) ? {
                    salesArea: params.cbCorporateInfo.salesArea || null,
                    fleet: params.cbCorporateInfo.fleet || null,
                    class: params.cbCorporateInfo.class || null,
                    sizingClass: params.cbCorporateInfo.sizingClass || null,
                    maxRentalNum: params.cbCorporateInfo.maxRentalNum || null,
                    contactPersons: params.cbCorporateInfo.contactPersons
                        .filter((cp: any) => !!cp.name && !!cp.surname)
                        .map((cp: any) => ({
                            cbCustomerId: cp.cbCustomerId || null,
                            bpCode: cp.bpCode || null,
                            name: cp.name.length > CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH ? cp.name.slice(0, CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH) : cp.name || null,
                            surname: cp.surname.length > CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH ? cp.surname.slice(0, CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH) : cp.surname || null,
                            address: cp.address || null,
                            houseNumber: cp.houseNumber || null,
                            country: cp.country || null,
                            region: cp.region || null,
                            city: cp.city || null,
                            postalCode: cp.postalCode || null,
                            phoneNumber: cp.phoneNumber || null,
                            mobile: cp.mobile || null,
                            email: cp.email || null,
                            delete: cp.delete || null
                        })) || []
                } : undefined
            });

        }
    }

    const { refetch, isFetching } = useQuery(['createCustomerBase', { queryFields }], createCustomerBase, {
        enabled: false,
        onSuccess: (data: any) => {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("message.success.default"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });

            if (isMobilityVoucherMode && !!onCustomerSubmit) onCustomerSubmit(data?.data?.data);
            else if (!!data?.data?.data?.cbCustomerId)
                navigate(`/customerbase/view/${data.data.data.cbCustomerId}`);

        },
        onError: (err: any) => {
            console.log(err);

            const code: string = getErrorKey(err);
            const sapMessage: string = err?.response?.data?.message || '';

            const isSapKO: boolean = code == CBSapCode.KO_CREATE;

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: isSapKO ? t(`message.warning.${code}`)?.concat(!!sapMessage ? ": [" + `${sapMessage}]` : '') : t(`message.error.${code}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });

            if (!isMobilityVoucherMode && isSapKO && !!err?.response?.data?.data?.cbCustomerId)
                setCbIdToSearchBySapKo(err.response.data.data.cbCustomerId);
        }
    });

    useEffect(() => {
        //console.log("CREATE QueryFields: ", queryFields);

        if (queryFields)
            refetch();
    }, [queryFields]);

    const handleCustomerSelected = useCallback((selectedCustomer: CustomerBaseDto) => {
        console.log("Selected Customer: ", selectedCustomer);

        formContext.clearErrors();
        if (cbContactPersonToDeleteArray.length > 0)
            setCbContactPersonToDeleteArray([]);

        setErrorSection({
            cbDetails: false,
            cbPersonalOrganizationInfo: false,
            cbInvoicingAddress: false,
            salesAreas: false,
            cbPaymentInfo: false,
            cbCorporateInfo: false
        });

        setDefaultSalesAreas([]);
        setDefaultBuagClasses([]);

        //SI AGGIORNANO SOLO I CAMPI MODIFICABILI
        if (partnerType == CBPartnerType.PERSON) {
            formContext.setValue("cbPersonalInfo.firstName", selectedCustomer.registry?.firstName || '');
            formContext.setValue("cbPersonalInfo.lastName", selectedCustomer.registry?.lastName || '');
            formContext.setValue("cbPersonalInfo.birthDate", selectedCustomer.registry?.birthDate || '');
            formContext.setValue("cbPersonalInfo.gender", selectedCustomer.registry?.gender || '');
            formContext.setValue("cbPersonalInfo.birthCountry", selectedCustomer.registry?.birthCountry || '');
            formContext.setValue("cbPersonalInfo.birthProvince", selectedCustomer.registry?.birthProvince || '');
            formContext.setValue("cbPersonalInfo.birthCity", selectedCustomer.registry?.birthCity || '');
            formContext.setValue("cbPersonalInfo.drvLicenceNumber", selectedCustomer.registry?.drvLicenceNumber || '');
            formContext.setValue("cbPersonalInfo.cfValidated", selectedCustomer.cfValidated || '');
        }

        formContext.setValue("cbPersonalInfo.taxCode", selectedCustomer.registry?.taxCode || '');
        formContext.setValue("cbPersonalInfo.publicAuthority", selectedCustomer.publicAuthority || CBYesNoType.NO);

        const residenceAddress = getResidenceAddress(selectedCustomer.contactInfo);

        formContext.setValue("cbPersonalInfo.residenceAddressId", residenceAddress?.id);
        formContext.setValue("cbPersonalInfo.residenceAddressNumber", residenceAddress?.addressNumber);
        formContext.setValue("cbPersonalInfo.residenceStreet", residenceAddress?.street || '');
        formContext.setValue("cbPersonalInfo.residenceHouseNumber", residenceAddress?.houseNumber || '');
        formContext.setValue("cbPersonalInfo.residenceZipCode", residenceAddress?.zipCode || '');
        formContext.setValue("cbPersonalInfo.residenceProvince", residenceAddress?.province || '');
        formContext.setValue("cbPersonalInfo.residenceCity", residenceAddress?.city || '');
        formContext.setValue("cbPersonalInfo.residenceCountry", residenceAddress?.country || '');
        formContext.setValue("cbPersonalInfo.residenceAddressType", residenceAddress?.addressType || CBAddressType.RESIDENCE);

        formContext.setValue("cbPersonalInfo.vatNumber", selectedCustomer.registry?.vatNumber || '');
        formContext.setValue("cbPersonalInfo.vatNumberCountry", selectedCustomer.registry?.vatNumberCountry || '');
        formContext.setValue("cbPersonalInfo.businessName", selectedCustomer.registry?.businessName || '');

        formContext.setValue("cbDetails.cbCustomerId", selectedCustomer.cbCustomerId || '');
        formContext.setValue("cbDetails.bpCode", selectedCustomer.bpCode || '');
        formContext.setValue("cbDetails.email", selectedCustomer.email || '');
        formContext.setValue("cbDetails.mobilePrefix", selectedCustomer.contactInfo?.prefix || '');
        formContext.setValue("cbDetails.mobile", selectedCustomer.contactInfo?.mobile || '');
        formContext.setValue("cbDetails.language", selectedCustomer.language || '');
        formContext.setValue("cbDetails.vip", selectedCustomer.vip || '');
        formContext.setValue("cbDetails.extMgmtReason", selectedCustomer.extMgmtReason || '');
        formContext.setValue("cbDetails.role", selectedCustomer.role || [CBRoleKey.PROSPECT]);

        if (partnerType == CBPartnerType.ORGANIZATION) {
            formContext.setValue("cbPersonalInfo.dealerType", selectedCustomer.dealerType || '');
            formContext.setValue("cbPersonalInfo.customerType", selectedCustomer.customerType || '');
            formContext.setValue("cbPersonalInfo.legalForm", selectedCustomer.legalForm || '');
            formContext.setValue("cbPersonalInfo.paIdNumber", selectedCustomer.paIdNumber || '');

            if (isFranceOrganization(selectedCustomer, true)) {
                formContext.setValue("cbPersonalInfo.siren", selectedCustomer?.registry?.siren || '');
                formContext.setValue("cbPersonalInfo.siret", selectedCustomer?.registry?.siret || '');
            }
            formContext.setValue("cbPersonalInfo.paIdNumber", selectedCustomer.paIdNumber || '');

            const c: any[] = selectedCustomer.corporateInfo?.contactPersons?.map((cp) => ({
                id: cp.id || null,
                bpCode: cp.bpCode || null,
                name: cp.name || '',
                surname: cp.surname || '',
                address: cp.address || '',
                houseNumber: cp.houseNumber || '',
                country: cp.country || '',
                region: cp.region || '',
                city: cp.city || '',
                postalCode: cp.postalCode || '',
                phoneNumber: cp.phoneNumber || '',
                mobile: cp.mobile || '',
                email: cp.email || '',
                delete: cp.delete || ''
            })) || [];

            formContext.setValue("cbCorporateInfo", {
                idZIT019: selectedCustomer.corporateInfo?.idZIT019 || null,
                maxRentalNum: selectedCustomer.corporateInfo?.maxRentalNum || '',
                class: selectedCustomer.corporateInfo?.class || '',
                fleet: selectedCustomer.corporateInfo?.fleet || '',
                salesArea: selectedCustomer.corporateInfo?.salesArea || '',
                sizingClass: selectedCustomer.corporateInfo?.sizingClass || '',
                contactPersons: c/*selectedCustomer.corporateInfo?.contactPersons?.map((cp) => ({
                    id: cp.id || null,
                    bpCode: cp.bpCode || null,
                    name: cp.name || '',
                    surname: cp.surname || '',
                    address: cp.address || '',
                    houseNumber: cp.houseNumber || '',
                    country: cp.country || '',
                    region: cp.region || '',
                    city: cp.city || '',
                    postalCode: cp.postalCode || '',
                    phoneNumber: cp.phoneNumber || '',
                    mobile: cp.mobile || '',
                    email: cp.email || '',
                    delete: cp.delete || ''
                })) || []*/
            });
            /** se non lo passo così nel form viene inserito solo il primo elemento dell'array */
            formContext.setValue("cbCorporateInfo.contactPersons", c);
        }

        const invoicingAddress = getInvoicingAddress(selectedCustomer.contactInfo);

        formContext.setValue("cbInvoicingAddress.invoicingAddressId", invoicingAddress?.id);
        formContext.setValue("cbInvoicingAddress.invoicingAddressNumber", invoicingAddress?.addressNumber);
        formContext.setValue("cbInvoicingAddress.invoicingStreet", invoicingAddress?.street || '');
        formContext.setValue("cbInvoicingAddress.invoicingHouseNumber", invoicingAddress?.houseNumber || '');
        formContext.setValue("cbInvoicingAddress.invoicingZipCode", invoicingAddress?.zipCode || '');
        formContext.setValue("cbInvoicingAddress.invoicingProvince", invoicingAddress?.province || '');
        formContext.setValue("cbInvoicingAddress.invoicingCity", invoicingAddress?.city || '');
        formContext.setValue("cbInvoicingAddress.invoicingCountry", invoicingAddress?.country || '');
        formContext.setValue("cbInvoicingAddress.invoicingAddrEmail", invoicingAddress?.addrEmail || '');
        formContext.setValue("cbInvoicingAddress.invoicingPec", invoicingAddress?.pec || '');
        formContext.setValue("cbInvoicingAddress.invoicingAddrMobile", invoicingAddress?.addrMobile || '');
        formContext.setValue("cbInvoicingAddress.invoicingTelephone", invoicingAddress?.telephone || '');
        formContext.setValue("cbInvoicingAddress.invoicingFax", invoicingAddress?.fax || '');
        formContext.setValue("cbInvoicingAddress.invoicingWebsite", invoicingAddress?.website || '');
        formContext.setValue("cbInvoicingAddress.invoicingCompanyFiscalCode", invoicingAddress?.companyFiscalCode || '');
        formContext.setValue("cbInvoicingAddress.invoicingRecipientCode", invoicingAddress?.recipientCode || '');
        formContext.setValue("cbInvoicingAddress.invoicingAddressType", invoicingAddress?.addressType || CBAddressType.INVOICING);

        const extraAddresses = extraAddressDtoToForm(selectedCustomer) || [];
        formContext.setValue("cbExtraAddresses", extraAddresses);
        setDefaultExtraAddresses(extraAddresses);

        formContext.setValue("cbPaymentInfo", selectedCustomer.paymentInfo?.map((pay) => ({
            id: pay.id || null,
            buagId: pay.buagId || null,
            buagClass: pay.buagClass || '',
            paymentMethod: pay.paymentMethod || '',
            paymentTerms: pay.paymentTerms || '',
            correspondingVariant: pay.correspondingVariant || '',
            iban: pay.iban || '',
            accountHolder: pay.accountHolder || '',
            referenceDetails: pay.referenceDetails || ''
        })).filter(pay => Object.values(pay).some((value: any) => !!value)) || []);

        formContext.setValue("salesAreas", selectedCustomer.salesAreas?.map((sa) => ({
            id: sa.id || null,
            salesOrganization: sa.salesOrganization || '',
            distributionChannel: sa.distributionChannel || '',
            division: sa.division || '',
            currency: sa.currency || ''
        })) || []);

        formContext.setValue("csData", selectedCustomer.csData || {});

    }, [formContext, partnerType, isCreationMode, cbContactPersonToDeleteArray]);


    const getCustomerByIDQuery = useQuery(['getCustomerBaseByIdWithSelect', cbIdToSearchBySapKo],
        {
            enabled: false,
            queryFn: () => {
                if (!!cbIdToSearchBySapKo)
                    return getCustomerBaseById(cbIdToSearchBySapKo);
            },
            onSuccess: (data: any) => {
                const customerbase: CustomerBaseDto = data?.data?.data;

                if (!customerbase)
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`message.error.creationUpdateGetRequest`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true
                            }
                        }
                    });

                handleCustomerSelected(customerbase);
                setCbIdToSearchBySapKo('');

            },
            onError: (error: any) => {

                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t(`message.error.creationUpdateGetRequest`),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });

                setCbIdToSearchBySapKo('');
            }
        });

    useEffect(() => {

        if (!!cbIdToSearchBySapKo)
            getCustomerByIDQuery.refetch();

    }, [cbIdToSearchBySapKo])

    const handleSubmit = () => {
        formContext.trigger().then((isValid) => {
            if (!isValid) {
                formContext.trigger("cbDetails").then((isValid) => {
                    setErrorSection((prev) => {
                        return { ...prev, ["cbDetails"]: !isValid }
                    });
                });
                formContext.trigger("cbPersonalInfo").then((isValid) => {
                    setErrorSection((prev) => {
                        return { ...prev, ["cbPersonalOrganizationInfo"]: !isValid }
                    });
                });
                formContext.trigger("cbInvoicingAddress").then((isValid) => {
                    setErrorSection((prev) => {
                        return { ...prev, ["cbInvoicingAddress"]: !isValid }
                    });
                });
                formContext.trigger("salesAreas").then((isValid) => {
                    setErrorSection((prev) => {
                        return { ...prev, ["salesAreas"]: !isValid }
                    });
                });
                formContext.trigger("cbPaymentInfo").then((isValid) => {
                    setErrorSection((prev) => {
                        return { ...prev, ["cbPaymentInfo"]: !isValid }
                    });
                });

                if (formContext.getValues("cbDetails.partnerType") == CBPartnerType.ORGANIZATION) {
                    formContext.trigger("cbCorporateInfo").then((isValid) => {
                        setErrorSection((prev) => {
                            return { ...prev, ["cbCorporateInfo"]: !isValid }
                        });
                    });
                }

            } else {
                setErrorSection({
                    cbDetails: false,
                    cbPersonalOrganizationInfo: false,
                    cbInvoicingAddress: false,
                    salesAreas: false,
                    cbPaymentInfo: false,
                    cbCorporateInfo: false
                });

                saveWithEmailScopeCheck(formContext.getValues());
            }
        });
    }

    /*
     * CR-DRIVALIA-EPCH (LPP-22837): l'e-mail di contatto puo' coincidere con quella
     * dell'indirizzo di fatturazione. Se l'operatore cambia la prima e le due coincidevano,
     * la scelta su cosa aggiornare spetta a lui: si chiede prima di salvare.
     * Se non coincidono (o l'e-mail non e' stata toccata) si salva senza interruzioni.
     */
    const saveWithEmailScopeCheck = (values: any) => {
        // CR-DRIVALIA-EPCH (LPP-22837): logica condivisa con CBMultiSectionActionButtons
        // (il Salva del dettaglio cliente passa da quel componente).
        if (shouldAskEmailScope(
                values?.cbDetails?.email,
                (formState.defaultValues as any)?.cbDetails?.email,
                values?.cbInvoicingAddress?.invoicingAddrEmail)) {
            setPendingEmailScopeValues(values);
            return;
        }

        onSubmit(values);
    }

    const resetButton = () => {
        return (
            <Button
                variant="outlined"
                color="accent"
                disabled={!formState.isDirty}
                onClick={() => {

                    formContext.reset();
                    setGoogleSearchBarClearValueState(true);
                    setErrorSection({
                        cbDetails: false,
                        cbPersonalOrganizationInfo: false,
                        cbInvoicingAddress: false,
                        salesAreas: false,
                        cbPaymentInfo: false,
                        cbCorporateInfo: false
                    });
                    if (cbContactPersonToDeleteArray.length > 0)
                        setCbContactPersonToDeleteArray([]);

                    setDefaultExtraAddresses([]);

                }}
            >
                {t("tabs.buttonLabels.reset")}
            </Button>
        );
    }

    const saveButton = () => {
        return (
            <Button
                color="accent"
                onClick={handleSubmit}
            >
                {t("tabs.buttonLabels.save")}
            </Button>
        );
    }

    return (
        <>
            <Box p={useTheme().spacing(1)}>
                <Box sx={{ borderBottom: 2, borderColor: 'divider' }}>
                    <Grid container alignItems={"center"} spacing={2}>
                        <Grid item md={9} sm={9} xs={12}>
                            <Tabs value={activeTab} onChange={handleChangeTab}>
                                {/* CR-DRIVALIA-EPCH (LPP-22837): `value` esplicito su ogni Tab.
                                    Senza di esso MUI assegna il valore per POSIZIONE, quindi con un tab
                                    nascosto (es. Car Sharing) gli indici slittano e il TabPanel atteso
                                    non viene mai reso: il contenuto del tab risultava vuoto. */}
                                <Tab value={CBTabIndex.CUSTOMER_DETAIL} label={t("tabs.tabsLabels.details")} {...allyProps(CBTabIndex.CUSTOMER_DETAIL)} />
                                {(!isCreationMode && (partnerType == CBPartnerType.PERSON)) && <Tab value={CBTabIndex.DOSSIER} label={t("tabs.tabsLabels.documents")} {...allyProps(CBTabIndex.DOSSIER)} />}
                                {(!isCreationMode && showCarSharingTab && (partnerType == CBPartnerType.PERSON)) &&
                                    hasCarSharingScope &&
                                    <Tab value={CBTabIndex.CAR_SHARING} label="Car Sharing" {...allyProps(CBTabIndex.CAR_SHARING)} />}
                                {!isCreationMode &&
                                    <Tab value={CBTabIndex.CHANGE_LOG} label={t("changeLog.tabLabel")} {...allyProps(CBTabIndex.CHANGE_LOG)} />}
                            </Tabs>
                        </Grid>
                        {!isCreationMode && (activeTab == CBTabIndex.CUSTOMER_DETAIL) &&
                            <Grid item md={3} sm={3} xs={12}>
                                <CBMultiSectionActionButtons />
                            </Grid>

                        }
                        {isCreationMode &&
                            <Grid item md={3} sm={3} xs={12}>
                                <Stack direction="row" alignItems={"center"} justifyContent={{ sm: "flex-end", xs: "flex-start" }} columnGap={1} mb={{ xs: 1 }}>
                                    {resetButton()}
                                    {saveButton()}
                                </Stack>
                            </Grid>
                        }
                    </Grid>
                </Box>
                <TabPanel value={activeTab} index={CBTabIndex.CUSTOMER_DETAIL}>
                    <CustomerBaseDetailsTab creationOf={creationOf} handleCustomerSelected={handleCustomerSelected} />
                </TabPanel>
                {!isCreationMode &&
                    <TabPanel value={activeTab} index={CBTabIndex.DOSSIER}>
                        <CustomerBaseDossiersTab />
                    </TabPanel>
                }
                {!isCreationMode && showCarSharingTab && (partnerType == CBPartnerType.PERSON) &&
                    <TabPanel value={activeTab} index={CBTabIndex.CAR_SHARING}>
                        <CustomerBaseCarSharingTab />
                    </TabPanel>
                }
                {!isCreationMode &&
                    <TabPanel value={activeTab} index={CBTabIndex.CHANGE_LOG}>
                        <CustomerBaseChangeLogTab cbCustomerId={cbCustomerId} />
                    </TabPanel>
                }
            </Box>
            {/* CR-DRIVALIA-EPCH (LPP-22837): scelta dello scope quando la nuova e-mail di contatto
                sostituisce una mail usata anche per la fatturazione. */}
            <CustomerBaseModal
                open={!!pendingEmailScopeValues}
                contentDescription={t("emailScopeModal.description", {
                    email: (formState.defaultValues as any)?.cbDetails?.email || ""
                })}
                buttonConfirmDescription={t("emailScopeModal.buttons.both")}
                buttonAlternativeDescription={t("emailScopeModal.buttons.onlyContact")}
                buttonCloseDescription={t("emailScopeModal.buttons.cancel")}
                onConfirm={() => {
                    const values = pendingEmailScopeValues;
                    setPendingEmailScopeValues(null);
                    onSubmit(values, true);
                }}
                onAlternative={() => {
                    const values = pendingEmailScopeValues;
                    setPendingEmailScopeValues(null);
                    onSubmit(values, false);
                }}
                onClose={() => setPendingEmailScopeValues(null)}
            />
            <CustomLoader loading={isFetching || getCustomerByIDQuery.isFetching} />
        </>
    );
}

export default CustomerBaseTabsManager;